/**
 * /field/pt/tests/:id?job=<jobId> — record one pressure test on the phone
 * (PT-3). Laid out after the April 2026 field mockups (Test Runner, Live
 * Monitor, Test Result): a big countdown, big start and end pressures in the
 * tenant's unit, a "Holding" line, and a pass/fail hero. No live chart and no
 * reading series — those arrive with digital recorder import.
 *
 * WHY `?job=`. There is no GET /tests/:id; a test is read from its job
 * (GET /jobs/:id carries `tests`). The job id rides in the query string so a
 * refresh mid-hold reloads the same test. Without it the screen says where to
 * open the test from, rather than guessing.
 *
 * THE HOLD IS THE SERVER'S. Start hold and Stop hold POST to /hold/start and
 * /hold/stop; the server stamps both instants and this screen never sends
 * started_at or ended_at (a field worker is refused for it: hold_server_only).
 * The countdown is read from the server's started_at (lib/pt-hold-clock.ts),
 * so a refresh, a locked phone or a killed tab resumes at the right second.
 *
 * THE VERDICT IS THE API'S. The drop is shown as the tech types the end
 * pressure — start minus end, from the two readings on this screen — because
 * that is what they need to see before pressing anything. It is labelled as
 * their reading. Once the API holds both pressures, its own `pressure_drop` is
 * shown instead. Pass and Fail are both always offered; the API refuses a pass
 * whose drop exceeds the allowable (`result_not_supported`) or that lacks a
 * required chart photo (`chart_required`), and the message is drawn under the
 * buttons as sent.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Pencil, PlayCircle, StopCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { usePtJob, useStartPtHold, useStopPtHold, useUpdatePtTest } from '../../../hooks/usePressureTesting';
import { formatHoldTime, useHoldClock } from '../../../lib/pt-hold-clock';
import { canWritePtTests, formatPressure, pressureFromInput, pressureInputValue } from '../../../lib/pressure-testing';
import type { PtJobDetail, PtPressureUnit, PtTest, PtTestResult } from '../../../types/pressure-testing';
import { PtChartCapture, PtChartImage } from '../PtChartPhoto';
import { ptWriteError, type PtWriteError } from '../PtJobDialogs';
import { PT_INPUT } from '../PtBits';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
  ptTestState,
  ptTestStateSkin,
} from './PtFieldBits';
import { PtTestSetupForm } from './PtTestSetupForm';

const BIG_INPUT = `${PT_INPUT} h-14 text-2xl font-bold tabular-nums`;
const LABEL = 'text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';

/** One big figure: label, value in the tenant's unit. */
function BigFigure({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div>
      <p className={LABEL}>{label}</p>
      <p className="mt-0.5 font-mono text-3xl font-extrabold tabular-nums text-white" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className={LABEL}>{label}</dt>
      <dd className="truncate text-sm text-fieldiq-text">{value}</dd>
    </div>
  );
}

const instrumentText = (i: { name: string | null; serial_number: string | null } | null | undefined) =>
  i ? `${i.name || '—'}${i.serial_number ? ` · ${i.serial_number}` : ''}` : 'None';

/** The guard's messages, or the one message, as a list. */
const refusalLines = (e: PtWriteError | null) => (e ? (e.errors.length ? e.errors.map((x) => x.error) : [e.message]) : []);

function HoldPanel({ test, unit, canWrite }: { test: PtTest; unit: PtPressureUnit | null; canWrite: boolean }) {
  const clock = useHoldClock(test.started_at, test.ended_at, test.hold_minutes);
  const start = useStartPtHold();
  const stop = useStopPtHold();
  const [startText, setStartText] = useState(() => pressureInputValue(test.start_pressure));
  const [localError, setLocalError] = useState<string | null>(null);
  const [error, setError] = useState<PtWriteError | null>(null);
  const pu = unit ?? '';

  const onStart = () => {
    setLocalError(null);
    setError(null);
    const typed = pressureFromInput(startText);
    if (typed === null && test.start_pressure == null) return setLocalError('Enter the start pressure before starting the hold.');
    if (typed !== null && Number.isNaN(typed)) return setLocalError('Start pressure must be a number.');
    start.mutate(
      { id: test.id, startPressure: typed },
      { onError: (err) => setError(ptWriteError(err, 'The hold was not started.')) },
    );
  };
  const onStop = () => {
    setError(null);
    stop.mutate({ id: test.id }, { onError: (err) => setError(ptWriteError(err, 'The hold was not stopped.')) });
  };

  const total = clock.totalSeconds === null ? '—' : formatHoldTime(clock.totalSeconds);

  if (clock.phase === 'idle') {
    return (
      <PtFieldCard className="space-y-4 px-4 py-4">
        <p className={LABEL}>Hold · {test.hold_minutes == null ? 'no hold length set' : `${test.hold_minutes} min`}</p>
        {canWrite ? (
          <>
            <label className="block">
              <span className={LABEL}>Start pressure{pu ? ` (${pu})` : ''}</span>
              <input className={`${BIG_INPUT} mt-1`} inputMode="decimal" value={startText} onChange={(e) => setStartText(e.target.value)} data-testid="pt-start-pressure" />
            </label>
            <PtRefusal message={localError} testId="pt-hold-local-error" />
            <PtRefusal message={error ? refusalLines(error)[0] : null} testId="pt-hold-error">
              {error && refusalLines(error).length > 1 && (
                <ul className="mt-1 list-disc pl-5">
                  {refusalLines(error).slice(1).map((m) => <li key={m}>{m}</li>)}
                </ul>
              )}
            </PtRefusal>
            <button type="button" className={`${PT_FIELD_PRIMARY} h-14 text-base`} onClick={onStart} disabled={start.isPending} data-testid="pt-start-hold">
              {start.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <PlayCircle className="size-6" aria-hidden="true" />}
              Start hold
            </button>
          </>
        ) : (
          <p className="text-sm text-fieldiq-text">The hold has not been started.</p>
        )}
      </PtFieldCard>
    );
  }

  const holding = clock.phase === 'holding';
  const countingDown = holding && clock.remainingSeconds !== null;
  return (
    <PtFieldCard className={`space-y-4 border-l-4 px-4 py-4 ${holding ? 'border-l-fi-amber' : 'border-l-fi-blue'}`}>
      <div className="flex items-center gap-2" role="status" data-testid="pt-hold-status">
        {holding && <span className="size-2.5 animate-pulse rounded-full bg-fi-signal-amber" aria-hidden="true" />}
        <span className="text-sm font-bold text-fieldiq-text">
          {holding ? (clock.holdMet ? 'Holding — hold time reached, stop the hold' : 'Holding') : 'Hold stopped'}
        </span>
      </div>
      <div className="text-center">
        <p className={LABEL}>{countingDown ? 'Remaining' : holding ? 'Elapsed' : 'Held'}</p>
        <p className="font-mono text-6xl font-extrabold tabular-nums text-white" data-testid="pt-hold-countdown">
          {countingDown ? formatHoldTime(clock.remainingSeconds) : formatHoldTime(clock.elapsedSeconds)}
        </p>
        <p className="mt-1 font-mono text-sm tabular-nums text-fieldiq-text" data-testid="pt-hold-total">
          {countingDown ? `${formatHoldTime(clock.elapsedSeconds)} elapsed / ${total} total` : `of ${total} hold`}
        </p>
      </div>
      <BigFigure label="Start pressure" value={formatPressure(test.start_pressure, unit)} testId="pt-start-figure" />
      {holding && canWrite && (
        <>
          <PtRefusal message={error?.message} testId="pt-hold-error" />
          <button type="button" className={`${PT_FIELD_PRIMARY} h-14 text-base`} onClick={onStop} disabled={stop.isPending} data-testid="pt-stop-hold">
            {stop.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <StopCircle className="size-6" aria-hidden="true" />}
            Stop hold
          </button>
        </>
      )}
    </PtFieldCard>
  );
}

function ResultPanel({ test, unit, canWrite }: { test: PtTest; unit: PtPressureUnit | null; canWrite: boolean }) {
  const update = useUpdatePtTest();
  const [endText, setEndText] = useState(() => pressureInputValue(test.end_pressure));
  const [localError, setLocalError] = useState<string | null>(null);
  const [error, setError] = useState<PtWriteError | null>(null);
  const pu = unit ?? '';
  const end = pressureFromInput(endText);

  // The tech's own reading until the API holds both pressures; then the API's figure.
  const apiDrop = test.pressure_drop;
  const typedDrop =
    test.start_pressure != null && end !== null && !Number.isNaN(end) ? test.start_pressure - end : null;
  const endChanged = endText !== pressureInputValue(test.end_pressure);
  const dropShown = !endChanged && apiDrop != null ? apiDrop : typedDrop;
  const dropLabel = !endChanged && apiDrop != null ? 'Pressure drop' : 'Drop (from your readings)';
  const overAllowable = dropShown !== null && test.allowable_drop != null && dropShown > test.allowable_drop;

  const record = (result: PtTestResult) => {
    setLocalError(null);
    setError(null);
    if (end !== null && Number.isNaN(end)) return setLocalError('End pressure must be a number.');
    const body: { result: PtTestResult; end_pressure?: number | null } = { result };
    if (endChanged) body.end_pressure = end;
    update.mutate(
      { id: test.id, body },
      {
        onSuccess: () => toast.success(result === 'pass' ? 'Recorded as a pass' : 'Recorded as a fail'),
        onError: (err) => setError(ptWriteError(err, 'The result was not saved.')),
      },
    );
  };

  return (
    <PtFieldCard className="space-y-4 px-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <BigFigure label="Start" value={formatPressure(test.start_pressure, unit)} />
        {canWrite ? (
          <label className="block">
            <span className={LABEL}>End pressure{pu ? ` (${pu})` : ''}</span>
            <input className={`${BIG_INPUT} mt-1`} inputMode="decimal" value={endText} onChange={(e) => setEndText(e.target.value)} data-testid="pt-end-pressure" />
          </label>
        ) : (
          <BigFigure label="End" value={formatPressure(test.end_pressure, unit)} />
        )}
      </div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 py-2" data-testid="pt-drop">
        <span className="text-sm text-fieldiq-text">
          {dropLabel}: <span className="font-mono font-bold tabular-nums">{formatPressure(dropShown, unit)}</span>
        </span>
        <span className="text-sm text-fieldiq-text">
          Allowable <span className="font-mono font-bold tabular-nums">{formatPressure(test.allowable_drop, unit)}</span>
        </span>
        {overAllowable && <p className="w-full text-xs text-fieldiq-text">This drop is more than the allowable. The API will refuse a pass.</p>}
      </div>
      {test.requires_recorder && !test.chart && (
        <p className="text-sm text-fieldiq-text" data-testid="pt-chart-required-note">
          This test type needs a chart photo before it can pass.
        </p>
      )}
      {canWrite && (
        <>
          <PtRefusal message={localError} testId="pt-result-local-error" />
          <PtRefusal message={error?.message} testId="pt-result-error" />
          <div className="grid grid-cols-2 gap-3">
            <button type="button" className={`${PT_FIELD_SECONDARY} h-14 text-base`} onClick={() => record('fail')} disabled={update.isPending} data-testid="pt-record-fail">
              <XCircle className="size-6" aria-hidden="true" />
              Fail
            </button>
            <button type="button" className={`${PT_FIELD_PRIMARY} h-14 text-base`} onClick={() => record('pass')} disabled={update.isPending} data-testid="pt-record-pass">
              {update.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-6" aria-hidden="true" />}
              Pass
            </button>
          </div>
        </>
      )}
    </PtFieldCard>
  );
}

/** The verdict, big — the Test Result mockup. Every figure is the API's. */
function ResultHero({ test, unit }: { test: PtTest; unit: PtPressureUnit | null }) {
  const pass = test.result === 'pass';
  const clock = useHoldClock(test.started_at, test.ended_at, test.hold_minutes);
  return (
    <PtFieldCard className={`border-l-4 px-4 py-5 ${pass ? 'border-l-fi-green' : 'border-l-fi-red'}`}>
      <div className="flex items-center gap-3" data-testid="pt-result-hero" data-result={test.result}>
        <span className={`grid size-14 place-items-center rounded-full border border-black/20 ${pass ? 'bg-fi-signal-green' : 'bg-fi-signal-red'}`} aria-hidden="true">
          {pass ? <CheckCircle2 className="size-8 text-fi-signal-ink" /> : <XCircle className="size-8 text-fi-signal-ink" />}
        </span>
        <div>
          <p className="text-3xl font-extrabold tracking-tight text-white">{pass ? 'PASS' : 'FAIL'}</p>
          <p className="text-sm text-fieldiq-text">{test.test_type_name || '—'}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <BigFigure label="Start" value={formatPressure(test.start_pressure, unit)} />
        <BigFigure label="End" value={formatPressure(test.end_pressure, unit)} />
        <BigFigure label="Drop" value={formatPressure(test.pressure_drop, unit)} />
        <BigFigure label="Allowable" value={formatPressure(test.allowable_drop, unit)} />
      </div>
      <p className="mt-3 font-mono text-sm tabular-nums text-fieldiq-text">
        Held {formatHoldTime(clock.elapsedSeconds)} / {clock.totalSeconds === null ? '—' : formatHoldTime(clock.totalSeconds)}
      </p>
    </PtFieldCard>
  );
}

export default function PtFieldTestScreen() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const jobId = params.get('job') ?? undefined;
  const { user } = useAuth();
  const query = usePtJob(jobId);
  const [editing, setEditing] = useState(false);

  const job: PtJobDetail | null = query.data?.data ?? null;
  const unit = query.data?.meta.pressure_unit ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const index = useMemo(() => job?.tests.findIndex((t) => t.id === id) ?? -1, [job, id]);
  const test = index >= 0 ? job!.tests[index] : null;
  const editable = job?.status === 'scheduled' || job?.status === 'active';
  const canWrite = editable && canWritePtTests(user, job) && !!unit;
  const state = test ? ptTestState(test) : null;

  // A setup edit belongs to a test whose hold has not started.
  useEffect(() => {
    if (state !== 'pending') setEditing(false);
  }, [state]);

  const title = test ? `${test.test_type_name || 'Test'} #${index + 1}` : 'Test';

  if (!jobId) {
    return (
      <PtFieldPage title="Test" backTo="/field/pt" testId="pt-field-test-screen">
        <PtFieldEmpty>
          Open this test from its job. <Link to="/field/pt" className="font-bold underline">My PT Jobs</Link>
        </PtFieldEmpty>
      </PtFieldPage>
    );
  }

  return (
    <PtFieldPage title={title} backTo={`/field/pt/jobs/${jobId}`} testId="pt-field-test-screen">
      <PtFieldQueryState query={query} subject="this test">
        {!job || !test ? (
          <PtFieldEmpty>This test is not on the job it was opened from.</PtFieldEmpty>
        ) : (
          <div className="space-y-3">
            <PtFieldCard className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className={LABEL}>{job.job_number || '—'} · {job.customer_name || '—'}</p>
                  <p className="text-lg font-bold text-white">{title}</p>
                </div>
                <PtFieldBadge skin={ptTestStateSkin(test)} testId="pt-test-state" />
              </div>
              {!editing && (
                <dl className="mt-3 grid grid-cols-2 gap-3">
                  <Spec label="Test pressure" value={formatPressure(test.test_pressure, unit)} />
                  <Spec label="Hold" value={test.hold_minutes == null ? '—' : `${test.hold_minutes} min`} />
                  <Spec label="Allowable drop" value={formatPressure(test.allowable_drop, unit)} />
                  <Spec label="Chart recorder" value={test.requires_recorder ? 'Required' : 'Optional'} />
                  <Spec label="Gauge" value={instrumentText(test.gauge)} />
                  <Spec label="Recorder" value={instrumentText(test.recorder)} />
                </dl>
              )}
              {canWrite && state === 'pending' && !editing && (
                <button type="button" className={`${PT_FIELD_SECONDARY} mt-3 h-11`} onClick={() => setEditing(true)} data-testid="pt-edit-setup">
                  <Pencil className="size-4" aria-hidden="true" />
                  Change setup
                </button>
              )}
              {editing && (
                <div className="mt-3">
                  <PtTestSetupForm jobId={job.id} test={test} unit={unit} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} />
                </div>
              )}
            </PtFieldCard>

            {(state === 'pending' || state === 'holding' || state === 'stopped') && (
              <>
                <PtFieldSectionTitle>Hold</PtFieldSectionTitle>
                <HoldPanel key={`${test.id}:${test.started_at ?? ''}`} test={test} unit={unit} canWrite={canWrite} />
              </>
            )}

            {state === 'stopped' && (
              <>
                <PtFieldSectionTitle>Result</PtFieldSectionTitle>
                <ResultPanel key={test.id} test={test} unit={unit} canWrite={canWrite} />
              </>
            )}

            {(state === 'pass' || state === 'fail') && <ResultHero test={test} unit={unit} />}

            <PtFieldSectionTitle>Chart photo</PtFieldSectionTitle>
            <PtFieldCard className="space-y-3 px-4 py-4">
              <PtChartImage test={test} timeZone={tz} />
              {canWrite && <PtChartCapture test={test} buttonClassName={PT_FIELD_SECONDARY} />}
            </PtFieldCard>

            {!editable && (
              <p className="text-sm text-fieldiq-text">This job is {job.status}; its tests can no longer be changed.</p>
            )}
          </div>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
